import { FC, useContext, useEffect, useState } from "react";

import { FormType, PeerToPeerQueryParams, RouteBuilders } from "@simplyk/common";

import { useLocaleContext } from "../../../contexts/LocaleContext";
import { trpc } from "../../../helpers/trpc";
import { useFormV2Context } from "../context/FormV2Context";
import { HeartsAnimationProvider } from "../context/heartsAnimationContext";

import { DonationFormV2CheckoutFooter } from "./CheckoutFooter/DonationFormV2CheckoutFooter";

import { NotificationBannerHeightContext } from "@/components/NotificationBanners/NotificationBannerHeightContext";
import { removeCampaignNavigationSearchParams } from "@/features/FormV2/helpers/campaignUrls";
import { useShouldHideFundraise } from "@/features/FormV2/hooks/useShouldHideFundraise";
import { useThemedFormZeffyLogoUrl } from "@/features/FormV2/hooks/useThemedFormZeffyLogoUrl";
import { useUtmForm } from "@/features/FormV2/hooks/useUtmForm";
import { ThemedHostedBy } from "@/features/FormV2/sharedComponents/HostedBy/ThemedHostedBy";
import { ThemedDonor } from "@/features/FormV2/sharedComponents/ThemedDonor";
import { ThemedDonorsListBlock } from "@/features/FormV2/sharedComponents/ThemedDonorsListBlock";
import { ThemedDonorsListDialog } from "@/features/FormV2/sharedComponents/ThemedDonorsListDialog";
import { ThemedFundraisersListBlock } from "@/features/FormV2/sharedComponents/ThemedFundraisersListBlock";
import { ThemedIndividualFundraiser } from "@/features/FormV2/sharedComponents/ThemedIndividualFundraiser";
import { ThemedTeamMemberCampaignsInformationDialog } from "@/features/FormV2/sharedComponents/ThemedTeamMemberCampaignsInformationDialog";
import {
  useThemedDonorListDialogServerState,
  useThemedDonorListDialogState,
} from "@/features/FormV2/sharedComponents/useThemedDonorListDialogState";
import { useThemedDonorsListBlockState } from "@/features/FormV2/sharedComponents/useThemedDonorsListBlockState";
import { Amounts } from "@/features/FormV2/Step1/DonationForm/Amounts";
import { useDonationFirstStepSubmit } from "@/features/FormV2/Step1/DonationForm/useDonationFirststepSubmit";
import { FormV2GenerateReceiptInfobox } from "@/features/FormV2/Step1/FormV2GenerateReceiptInfobox";
import { FormV2LogoChip } from "@/features/FormV2/Step1/FormV2LogoChip";
import { FormV2ZeffyBusinessModelInfobox } from "@/features/FormV2/Step1/FormV2ZeffyBusinessModelInfobox";
import { MobileFormV2Banner } from "@/features/FormV2/Step1/MobileFormV2Banner";
import { CampaignIsLiveDialog } from "@/features/FormV2/Step1/PeerToPeer/CampaignIsLiveDialog/CampaignIsLiveDialog";
import { useCampaignCreatedSuccess } from "@/features/FormV2/Step1/PeerToPeer/CampaignIsLiveDialog/useCampaignCreatedSuccess";
import { FundraiseDialog } from "@/features/FormV2/Step1/PeerToPeer/FundraiseDialog/FundraiseDialog";
import { PeerToPeerRegisterCampaignAction } from "@/features/FormV2/Step1/PeerToPeer/PeerToPeerRegisterCampaignAction";
import { PeerToPeerTeamCampaignFormV2Summary } from "@/features/FormV2/Step1/PeerToPeer/PeerToPeerTeamCampaignFormV2Summary";
import { PeerToPeerTeamCampaignFundraiseCampaignAction } from "@/features/FormV2/Step1/PeerToPeer/PeerToPeerTeamCampaignFundraiseCampaignAction";
import { useOpenPeerToPeerRegistrationForm } from "@/features/FormV2/Step1/PeerToPeer/useOpenPeerToPeerRegistrationForm";
import { PeerToPeerFormV2TeamCampaignStep1Layout } from "@/features/FormV2/Step1/PeerToPeerFormV2TeamCampaignStep1Layout";
import { useTrackThemedFormLogoClicked } from "@/features/FormV2/tracking/useTrackThemedFormLogoClicked";
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/context/PreviewContext";
import { parseAsBoolean, parseAsString, parseAsStringLiteral, useQueryState } from "@/hooks/useQueryState";
import { useUrlBuilder } from "@/hooks/useUrlBuilder";

export type PeerToPeerFormV2TeamCampaignStep1Props = {
  isEmbed?: boolean;
};

export const PeerToPeerFormV2TeamCampaignStep1: FC<PeerToPeerFormV2TeamCampaignStep1Props> = ({ isEmbed = false }) => {
  const { isoLocale } = useLocaleContext();
  const { formObject, hasBanner, formCurrency, formCategory } = useFormV2Context(FormType.DonationForm);
  const { isExpanded, bannerHeight } = useContext(NotificationBannerHeightContext);
  const { isPreview } = usePreviewContext();
  const { canSubmitFirstStep } = useDonationFirstStepSubmit();
  const urlBuilder = useUrlBuilder();

  const [searchQuery, setSearchQuery] = useQueryState("search", parseAsString.withDefault(""));
  const [teamMemberCampaignsSearchQuery, setTeamMemberCampaignsSearchQuery] = useQueryState(
    "teamMemberCampaignsSearch",
    parseAsString.withDefault("")
  );
  const [searchText, setSearchText] = useState(searchQuery);
  const [teamMemberCampaignsSearchText, setTeamMemberCampaignsSearchText] = useState(teamMemberCampaignsSearchQuery);
  const [fundraiseQuery, setFundraiseQuery] = useQueryState(
    PeerToPeerQueryParams.OpenFundraiser,
    parseAsBoolean.withDefault(false).withOptions({ history: "push" })
  );
  const [teamMemberCampaignsQuery, setTeamMemberCampaignsQuery] = useQueryState(
    "teamMemberCampaigns",
    parseAsStringLiteral(["view-all"] as const).withOptions({ history: "push" })
  );

  const { hasDonations, hasMoreDonations, latestDonations } = useThemedDonorsListBlockState({
    donationFormId: formObject.id,
  });

  const {
    open: donorsListDialogOpen,
    onOpen: onDonorsListDialogOpen,
    onClose: onDonorsListDialogClose,
  } = useThemedDonorListDialogState();

  const donorsDialogServerState = useThemedDonorListDialogServerState({
    donationFormId: formObject.id,
    open: donorsListDialogOpen,
  });

  const utm = useUtmForm();

  const onTrackLogoClick = useTrackThemedFormLogoClicked({
    formType: FormType.DonationForm,
    formCategory,
    country: formObject.organization?.country,
    formStep: 1,
  });

  const logoHref = useThemedFormZeffyLogoUrl(utm);

  const { isCampaignIsLiveDialogOpen, handleCampaignIsLiveDialogClose, formLink } = useCampaignCreatedSuccess({
    isIndividualCampaign: false,
  });

  const isTeamMemberCampaignsModalOpen = teamMemberCampaignsQuery === "view-all";

  const { data: getTeamMemberCampaignsResponse } = trpc.getTeamMemberCampaigns.useQuery({
    teamId: formObject.id!,
    cursor: 0,
    limit: 6,
  });

  const teamMemberCampaigns = getTeamMemberCampaignsResponse ?? { items: [], hasMore: false };

  const { data: searchedCampaignsData, isFetching: isSearchedCampaignsFetching } =
    trpc.searchPeerToPeerCampaigns.useInfiniteQuery(
      {
        campaignId: formObject.campaignId!,
        limit: 10,
        search: searchQuery,
        teamId: formObject.id!,
      },
      {
        enabled: !!searchQuery && !isTeamMemberCampaignsModalOpen,
        initialCursor: 0,
        getNextPageParam: (lastPage) => lastPage?.nextCursor,
      }
    );

  const searchedCampaigns =
    searchedCampaignsData?.pages
      ?.filter((x) => !!x)
      ?.map((x) => x!.items)
      ?.flat()
      ?.filter((x) => x.type !== "team")
      ?.map((x) => ({
        ...x,
        href: urlBuilder({
          route: RouteBuilders.buildPeerToPeerV2IndividualCampaignLink({ isoLocale, path: x.path }),
          searchParams: removeCampaignNavigationSearchParams,
        }),
      })) ?? [];

  const searchedTeamMemberCampaigns = searchedCampaigns ?? { items: [], hasMore: false };

  const shouldDisplayTeamMemberCampaigns = teamMemberCampaigns.items.length > 0;

  const hasMandatoryRegistration = formObject.campaign?.hideFundraiseButton ?? formObject.hideFundraiseButton;
  const shouldHideFundraise = useShouldHideFundraise();
  const shouldShowFundraiseButton = formObject.campaign?.allowJoinTeam && !shouldHideFundraise;

  const openPeerToPeerRegistrationForm = useOpenPeerToPeerRegistrationForm(formObject.ticketingId ?? undefined);

  useEffect(() => {
    // Debounce the search to avoid spamming the server with requests
    let timeout: NodeJS.Timeout;

    if (searchQuery !== searchText) {
      timeout = setTimeout(() => {
        setSearchQuery(searchText);
      }, 400);
    }

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  useEffect(() => {
    // Debounce the search to avoid spamming the server with requests
    let timeout: NodeJS.Timeout;

    if (teamMemberCampaignsSearchQuery !== teamMemberCampaignsSearchText) {
      timeout = setTimeout(() => {
        setTeamMemberCampaignsSearchQuery(teamMemberCampaignsSearchText);
      }, 400);
    }

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamMemberCampaignsSearchText]);

  const renderTeamMemberCampaignsTile = () => (
    <ThemedFundraisersListBlock
      isLoading={isSearchedCampaignsFetching}
      searchText={searchText}
      onSearchTextChange={setSearchText}
      onViewAllClick={() => setTeamMemberCampaignsQuery("view-all")}
      showViewAllButton={teamMemberCampaigns?.hasMore}
      mode="show-only-individual-campaigns"
    >
      {(searchQuery ? searchedTeamMemberCampaigns : teamMemberCampaigns.items).map(
        ({ path, ...teamMemberCampaign }, index) => {
          return (
            <ThemedIndividualFundraiser
              key={index}
              {...teamMemberCampaign}
              href={urlBuilder({
                route: RouteBuilders.buildPeerToPeerV2IndividualCampaignLink({ isoLocale, path }),
                searchParams: removeCampaignNavigationSearchParams,
              })}
              imageSrc={teamMemberCampaign.imageSrc}
              campaignTitle={teamMemberCampaign.campaignTitle}
              totalRaised={teamMemberCampaign.totalRaised}
              isPartOfTeam={true}
              currency={formCurrency}
            />
          );
        }
      )}
    </ThemedFundraisersListBlock>
  );

  return (
    <HeartsAnimationProvider>
      <PeerToPeerFormV2TeamCampaignStep1Layout
        isEmbed={isEmbed}
        contextDrawerHeight={isExpanded ? bannerHeight : 0}
        slots={{
          banner: hasBanner ? <MobileFormV2Banner /> : undefined,
          organizationLogo: (
            <FormV2LogoChip organizationName={formObject.organization?.name} logoUrl={formObject.logoUrl} />
          ),
          summary: <PeerToPeerTeamCampaignFormV2Summary forceInlined={isEmbed} />,
          teamMemberCampaigns: shouldDisplayTeamMemberCampaigns ? renderTeamMemberCampaignsTile() : undefined,
          donors: hasDonations && (
            <ThemedDonorsListBlock showViewAllButton={hasMoreDonations} onViewAllClick={onDonorsListDialogOpen}>
              {latestDonations.map((donation, index) => (
                <ThemedDonor {...donation} key={index} />
              ))}
            </ThemedDonorsListBlock>
          ),
          hostedBy: <ThemedHostedBy campaignId={formObject.campaignId!} teamId={formObject.teamId ?? undefined} />,
          helpFundraise: hasMandatoryRegistration ? (
            <PeerToPeerRegisterCampaignAction onClick={openPeerToPeerRegistrationForm} />
          ) : (
            shouldShowFundraiseButton && (
              <PeerToPeerTeamCampaignFundraiseCampaignAction onClick={() => setFundraiseQuery(true)} />
            )
          ),
          donationAmounts: <Amounts />,
          generateReceipt: <FormV2GenerateReceiptInfobox />,
          zeffyBusinessModel: <FormV2ZeffyBusinessModelInfobox />,
          checkoutFooter: <DonationFormV2CheckoutFooter />,
        }}
        showCheckoutFooter={canSubmitFirstStep && !isPreview}
        logoHref={logoHref}
        onLogoClick={onTrackLogoClick}
      />
      <ThemedTeamMemberCampaignsInformationDialog
        campaignId={formObject.campaignId!}
        teamId={formObject.id!}
        open={isTeamMemberCampaignsModalOpen}
        search={teamMemberCampaignsSearchQuery}
        onSearchChange={setTeamMemberCampaignsSearchText}
        formCurrency={formCurrency}
        onClose={() => {
          setTeamMemberCampaignsQuery(null);
          setTeamMemberCampaignsSearchQuery(null);
        }}
      />

      <FundraiseDialog
        open={fundraiseQuery}
        onClose={() => setFundraiseQuery(null)}
        campaignId={formObject.campaignId as string}
        allowTeamCreation={Boolean(formObject.campaign?.allowTeamCreation)}
        allowFundraiserCreation={Boolean(formObject.campaign?.allowFundraiserCreation)}
        allowJoinTeam={Boolean(formObject.campaign?.allowJoinTeam)}
        defaultTeamId={formObject.id}
      />
      {hasDonations && (
        <ThemedDonorsListDialog
          {...donorsDialogServerState}
          open={donorsListDialogOpen}
          onClose={onDonorsListDialogClose}
        />
      )}
      {/* Display the campaign is live dialog if the p2p campaign is created successfully */}
      <CampaignIsLiveDialog
        open={isCampaignIsLiveDialogOpen}
        onClose={handleCampaignIsLiveDialogClose}
        isTeamCampaign={true}
        isIndividualCampaign={false}
        fundraiserName={formObject.donationFormFields?.[0]?.title}
        formLink={formLink}
      />
    </HeartsAnimationProvider>
  );
};
